// Truck counts as the office sees them (truck-stock parity 1+2), kept apart from the screen so they
// are tested: what a count asked for or sent in says, what still needs a reason, and a refill's gaps.

/** stockCounts.open's count. */
export interface OpenCount {
  id: string;
  locationId: string;
  locationName: string;
  status: 'requested' | 'submitted' | 'posted' | 'discarded';
  scope: 'full' | 'spot';
  requestedAt: string | null;
  countedAt: string | null;
  submittedAt: string | null;
  lines: {
    itemId: string;
    name: string;
    requested: boolean;
    counted: string | null;
    expected: string | null;
    difference: string | null;
    /** Counted again on the truck since (QA-863); once posted, this line is left as that count left it. */
    countedAgain?: {
      countId: string;
      countedAt: string;
      counted: string;
      countedBy: string | null;
      posted: boolean;
    } | null;
    /** The earlier count still to post that this one counts the item again for (QA-863). */
    countedBefore?: { countId: string; countedAt: string; counted: string; countedBy: string | null } | null;
  }[];
  /** Who sent it in (QA-863). */
  countedBy?: string | null;
}

/** stockCountOrders.list's refill. */
export interface Refill {
  id: string;
  countId: string;
  locationId: string;
  status: 'draft' | 'committed' | 'discarded';
  lines: {
    itemId: string;
    name: string;
    quantity: string;
    source: 'warehouse' | 'vendor';
    vendorId: string | null;
    poId: string | null;
    warehouseOnHand: string;
  }[];
}

const clock = (iso: string, timeZone?: string) =>
  new Intl.DateTimeFormat('en-CA', {
    ...(timeZone ? { timeZone } : {}),
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));

/** "2 items, asked for Jul 2, 10:00 a.m.: not counted yet", or when it was counted and sent in. */
export function countWhen(c: OpenCount, timeZone?: string): string {
  const n = c.lines.filter((l) => l.requested).length;
  const what = c.scope === 'spot' ? `${n} ${n === 1 ? 'item' : 'items'}` : 'The whole truck';
  if (c.status === 'requested')
    return `${what}, asked for ${c.requestedAt ? clock(c.requestedAt, timeZone) : ''}: not counted yet.`;
  const counted = c.countedAt ? `counted ${clock(c.countedAt, timeZone)}` : 'counted';
  // "9:30 a.m." already ends the sentence
  const end = (t: string) => (t.endsWith('.') ? t : `${t}.`);
  const who = c.countedBy ? ` by ${c.countedBy}` : '';
  return end(
    c.requestedAt ? `${what}, ${counted}${who}` : `Sent in by ${c.countedBy ?? 'the technician'}, ${counted}`,
  );
}

const plain = (q: string) => String(Number(q));

/**
 * How a line stands against the item's other counts on the truck (QA-863): "Counted again Jul 2,
 * 10:38 a.m. by Sam Rivera: 3, posted. This line is left as it." or, on the recount, "Rechecks
 * the count of Jul 2, 10:37 a.m. by Sam Rivera: 2 then, 3 now."; null when no other count has it.
 */
export function countedElsewhere(l: OpenCount['lines'][number], timeZone?: string): string | null {
  const by = (n: string | null) => (n ? ` by ${n}` : '');
  if (l.countedAgain) {
    const a = l.countedAgain;
    const when = `Counted again ${clock(a.countedAt, timeZone)}${by(a.countedBy)}: ${plain(a.counted)}`;
    return a.posted
      ? `${when}, posted. Posting this count leaves it as that one did.`
      : `${when}, still to post. Its figure stands, whichever is posted first.`;
  }
  if (l.countedBefore && l.counted !== null) {
    const b = l.countedBefore;
    return `Rechecks the count of ${clock(b.countedAt, timeZone)}${by(b.countedBy)}: ${plain(b.counted)} then, ${plain(l.counted)} now.`;
  }
  return null;
}

/** The items of a sent-in count that differ from the books and have no reason picked yet. */
export function reasonsMissing(c: OpenCount, reasons: Record<string, string>): string[] {
  return c.lines
    .filter((l) => l.difference !== null && Number(l.difference) !== 0 && !reasons[l.itemId])
    .map((l) => l.itemId);
}

/** What stops a refill being committed, in words; none: it can go. */
export function refillProblems(r: Refill): string[] {
  if (!r.lines.length) return ['Nothing is left on it: discard it.'];
  const noVendor = r.lines.filter((l) => l.source === 'vendor' && !l.vendorId).map((l) => l.name);
  return noVendor.length ? [`Pick a vendor, or take it from the warehouse: ${noVendor.join(', ')}.`] : [];
}

/** A line the warehouse can't cover in full: it says so beside "From the warehouse". */
export const warehouseShort = (l: Refill['lines'][number]) =>
  l.source === 'warehouse' && Number(l.warehouseOnHand) < Number(l.quantity);
